import React, { useState } from 'react';
import { StoreProvider, useStore } from './state/store';
import { ActionTheater } from './theater/ActionTheater';
import { Badge, Button, StatusDot, usePresentationMode } from './design/primitives';
import { Overview } from './pages/Overview';
import { Payments } from './pages/Payments';
import { Retail } from './pages/Retail';
import { Lending } from './pages/Lending';
import { Compliance } from './pages/Compliance';
import { ITOps } from './pages/ITOps';
import { DevOps } from './pages/DevOps';
import { CloudPlatform } from './pages/CloudPlatform';
import { Fleet } from './pages/Fleet';
import { SignIn } from './pages/SignIn';
import './design/base.css';
import './design/app.css';
import './design/pages.css';

/**
 * The bank's back-office console.
 *
 * Three fixed zones: navigation on the left, the bank's own work in the middle,
 * the Action Theater pinned on the right. The theater never scrolls out of view,
 * because the whole argument of the demo is that the two are happening at the
 * same time — a security panel you have to go looking for reads as an
 * afterthought, which is precisely the wrong impression.
 */

interface NavItem {
  id: string;
  label: string;
  eyebrow: string;
  group: string;
  render: () => React.ReactNode;
}

const NAV: NavItem[] = [
  { id: 'overview', label: 'Overview', eyebrow: 'Start here', group: 'Start here', render: () => <Overview /> },
  { id: 'payments', label: 'Payments & Cards', eyebrow: 'Banking', group: 'Banking', render: () => <Payments /> },
  { id: 'retail', label: 'Retail & Support', eyebrow: 'Banking', group: 'Banking', render: () => <Retail /> },
  { id: 'lending', label: 'Lending', eyebrow: 'Banking', group: 'Banking', render: () => <Lending /> },
  { id: 'compliance', label: 'KYC & Compliance', eyebrow: 'Banking', group: 'Banking', render: () => <Compliance /> },
  { id: 'itops', label: 'IT Operations', eyebrow: 'Technology', group: 'Technology', render: () => <ITOps /> },
  { id: 'devops', label: 'DevOps & CI/CD', eyebrow: 'Technology', group: 'Technology', render: () => <DevOps /> },
  { id: 'cloud', label: 'Cloud & AI Platform', eyebrow: 'Technology', group: 'Technology', render: () => <CloudPlatform /> },
  { id: 'fleet', label: 'Machine Fleet', eyebrow: 'Technology', group: 'Technology', render: () => <Fleet /> },
];

export default function App() {
  return (
    <StoreProvider>
      <Shell />
    </StoreProvider>
  );
}

function Shell() {
  const { status, error, bootstrap, approvals, setOperator } = useStore();
  const [presentation, togglePresentation] = usePresentationMode();
  const [active, setActive] = useState('overview');

  if (status === 'loading') {
    return (
      <div className="mb-boot">
        <p>Opening the Meridian operations console…</p>
      </div>
    );
  }

  if (status === 'locked') {
    return <SignIn />;
  }

  if (status === 'error' || !bootstrap) {
    return (
      <div className="mb-boot">
        <p>The console could not start.</p>
        <p className="mb-boot__detail">{error}</p>
      </div>
    );
  }

  const current = NAV.find(item => item.id === active) ?? NAV[0];
  const groups = [...new Set(NAV.map(item => item.group))];
  const live = bootstrap.controlPlane.mode === 'live';

  return (
    <div className="mb-app">
      <a className="mb-skip-link" href="#main">
        Skip to content
      </a>

      <header className="mb-brand">
        <span className="mb-brand__mark" aria-hidden>
          M
        </span>
        <span className="mb-brand__name">{bootstrap.bank.name}</span>
      </header>

      <div className="mb-header">
        <div className="mb-header__context">
          <span className="mb-header__eyebrow">{current.eyebrow}</span>
          <h1 className="mb-header__title">{current.label}</h1>
        </div>

        <div className="mb-header__spacer" />

        <label className="mb-operator">
          <span className="mb-operator__label">Acting as</span>
          <select
            className="mb-select mb-operator__select"
            value={bootstrap.session.operatorId}
            onChange={event => void setOperator(event.target.value)}
            aria-label="Operator identity"
          >
            {bootstrap.estate.staff.map((person: any) => (
              <option key={person.id} value={person.id}>
                {person.fullName} — {person.role}
              </option>
            ))}
          </select>
        </label>

        {approvals.length > 0 && <Badge tone="approval">{approvals.length} awaiting approval</Badge>}

        <span className="mb-control-plane" title={live ? bootstrap.controlPlane.apiUrl : undefined}>
          <StatusDot
            status={live ? 'online' : 'offline'}
            label={live ? 'Control plane connected' : 'Control plane not configured'}
          />
          {live ? 'Governed live by FullCourtDefense' : 'Control plane not configured'}
        </span>

        <Button
          variant="subtle"
          size="sm"
          onClick={togglePresentation}
          ariaPressed={presentation}
          title="Larger type and looser spacing, for projecting"
        >
          {presentation ? 'Exit presentation' : 'Presentation mode'}
        </Button>
      </div>

      <nav className="mb-nav" aria-label="Bank areas">
        {groups.map(group => (
          <div key={group}>
            <div className="mb-nav__group-label">{group}</div>
            {NAV.filter(item => item.group === group).map(item => (
              <button
                key={item.id}
                className="mb-nav__item"
                aria-current={item.id === active ? 'page' : undefined}
                onClick={() => setActive(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        ))}

        <div className="mb-nav__footer">
          <a href={bootstrap.controlPlane.dashboardUrl} target="_blank" rel="noreferrer noopener">
            Open the AgentGuard console →
          </a>
          <p>
            Everything here is fictional. No real customer data, no real hosts, and no command on this site reaches a
            real shell.
          </p>
        </div>
      </nav>

      <main className="mb-main" id="main">
        {current.render()}
      </main>

      <aside className="mb-stream" aria-label="Action Theater">
        <ActionTheater />
      </aside>
    </div>
  );
}
